import React, { useState } from 'react';
import { v4 as UUID } from 'uuid';
import { CustomInput } from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import ThemedSearchInput from "../Components/SearchInput";

function ArtifactParameters({
  parameters,
  setParameters,
  canEdit,
  isNew,
  artifactId,
  artifactName,
  artifactType,
  setLoading,
  showAlert,
  showConfirmation,
  updateArtifactById
}) {

  const [newParameter, setNewParameter] = useState({ name: '', description: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingData, setEditingData] = useState({ name: '', description: '' });


  const saveParametersToApi = async (updatedParams, successMessage) => {
    if (isNew) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', artifactName);
      formData.append('type', artifactType);
      formData.append(
        'parameters',
        JSON.stringify(updatedParams.map(({ key, name, description }) => ({ key, name, description })))
      );

      await updateArtifactById(artifactId, formData);
      showAlert('success', successMessage);
    } catch (error) {
      console.error('Error saving parameter:', error);
      showAlert('fail', 'Failed to save parameter');
    } finally {
      setLoading(false);
    }
  };

  const onAddParameter = async () => {
    if (!newParameter.name.trim() || !newParameter.description.trim()) {
      showAlert('fail', 'Please enter both name and description');
      return;
    }

    const updatedParams = [
      ...parameters,
      {
        key: UUID(),
        name: newParameter.name,
        description: newParameter.description,
      },
    ];

    setParameters(updatedParams);
    setNewParameter({ name: '', description: '' });

    await saveParametersToApi(updatedParams, 'Parameter saved successfully');
  };

  const handleEditClick = (parameter) => {
    setEditingKey(parameter.key);
    setEditingData({ name: parameter.name, description: parameter.description });
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditingData({ name: '', description: '' });
  };

  const handleEditingDataChange = (field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingData.name.trim() || !editingData.description.trim()) {
      showAlert('fail', 'Please enter both name and description');
      return;
    }

    const updatedParams = parameters.map(param =>
      param.key === editingKey
        ? { ...param, name: editingData.name, description: editingData.description }
        : param
    );

    setParameters(updatedParams);
    handleCancelEdit();

    await saveParametersToApi(updatedParams, 'Parameter updated successfully');
  };

  const onDelete = (parameterKey) => {
    if (!parameterKey) return;

    showConfirmation(
      `Are you sure you want to delete the item?`,
      async () => {
        const updatedParams = parameters.filter(param => param.key !== parameterKey);

        setParameters(updatedParams);

        try {
            await saveParametersToApi(updatedParams, 'Item was successfully deleted!');
        } catch (error) {
            showAlert('fail', 'Failed to delete the item!');
        }
      }
    );
  }

  const filteredParameters = parameters.filter(parameter =>
    parameter.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="row mt-4">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h3 className="h3-title">Add New Parameter</h3>

          <div className="input-block">
            <CustomInput
              id="new-parameter"
              label="Parameter"
              placeholder="Enter parameter"
              value={newParameter.name}
              onChange={(id, val) =>
                setNewParameter((prev) => ({ ...prev, name: val }))
              }
              disabled={!canEdit}
            />
          </div>

          <div className="input-block">
            <CustomInput
              id="new-description"
              label="Description"
              placeholder="Enter description"
              value={newParameter.description}
              onChange={(id, val) =>
                setNewParameter((prev) => ({ ...prev, description: val }))
              }
              disabled={!canEdit}
            />
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button
              variant="primary"
              size="medium"
              disabled={!canEdit}
              onClick={onAddParameter}
            >
              Add Parameter
            </Button>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-6 radius-yes p-3 bg-yes">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="h3-title mb-4">Parameters</h3>
            <ThemedSearchInput
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search by name"
            />
          </div>

          {parameters.length === 0 ? (
            <p className="text-muted">No parameters available.</p>
          ) : (
            <>
              <div className="term-header-row">
                <div className="term-keyword">Parameter</div>
                <div className="term-definition">Description</div>
              </div>
              <div
                style={{
                  maxHeight: parameters.length > 5 ? '330px' : 'auto',
                  overflowY: parameters.length > 5 ? 'auto' : 'visible'
                }}
              >
                {filteredParameters.map((parameter) => (
                  <div key={parameter.key} className="term-item">
                    {editingKey === parameter.key ? (
                      <>
                        <div className="term-keyword" style={{ flex: 1, marginRight: '10px', alignSelf: 'center' }}>
                          <CustomInput
                            id={`edit-name-${parameter.key}`}
                            label=""
                            placeholder="Parameter name"
                            type="text"
                            value={editingData.name}
                            onChange={(id, value) => handleEditingDataChange('name', value)}
                          />
                        </div>
                        <div className="term-definition" style={{ flex: 2, marginRight: '10px', alignSelf: 'center' }}>
                          <CustomInput
                            id={`edit-desc-${parameter.key}`}
                            label=""
                            placeholder="Parameter description"
                            type="text"
                            value={editingData.description}
                            onChange={(id, value) => handleEditingDataChange('description', value)}
                          />
                        </div>

                        <div className="d-flex" style={{ marginLeft: 'auto', alignItems: 'center' }}>
                          <button
                            onClick={handleSaveEdit}
                            title="Save changes"
                            style={{ border: 'none', background: 'transparent', padding: '0 8px' }}
                          >
                            <i className="fa-solid fa-check" style={{ color: '#b09de6', cursor: 'pointer' }}></i>
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            title="Cancel editing"
                            style={{ border: 'none', background: 'transparent', padding: '0 8px' }}
                          >
                            <i className="fa-solid fa-times" style={{ color: '#ef6565', cursor: 'pointer' }}></i>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="term-keyword">{parameter.name}</div>
                        <div className="term-definition">{parameter.description}</div>
                        <button
                          className="edit-button"
                          onClick={() => handleEditClick(parameter)}
                          title="Edit parameter"
                          style={{ border: 'none', background: 'transparent', marginLeft: '6px' }}
                          disabled={!canEdit}
                        >
                          <i className="fa-solid fa-pencil" style={{ color: '#b09de6' }}></i>
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => onDelete(parameter.key)}
                          title="Delete parameter"
                          disabled={!canEdit}
                        ></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default ArtifactParameters;
